/* eslint-disable react/jsx-props-no-spreading */
import { Button, ButtonProps, useToken } from '@chakra-ui/react';
import { forwardRef } from 'react';

import { hexToRGBA } from '@/client/utils/hexToRGBA';

// forwardRef so this can serve focus-management call sites — e.g. an
// AlertDialog's leastDestructiveRef on a Cancel button. Behavior is unchanged
// for the ref-less callers.
const OutlineButton = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  const [brandButton] = useToken('colors', ['brand.button']);
  // Pull the object-valued pseudo-props out so they can be merged rather
  // than wholesale-replaced (Chakra replaces these objects, not merges).
  const { _hover: hoverOverride, _disabled: disabledOverride, ...rest } = props;

  // Scalar defaults come before `{...rest}` so callers can override them
  // (e.g. a tighter `paddingX` or a fixed `minWidth`). `_hover` / `_disabled`
  // come after and merge with any caller value, so a caller can extend them
  // without silently dropping the brand styling underneath.
  return (
    <Button
      ref={ref}
      border="2px solid"
      borderColor="brand.button"
      backgroundColor="transparent"
      color="brand.button"
      borderRadius="md"
      paddingY={4}
      paddingX={8}
      fontWeight={500}
      {...rest}
      _disabled={{
        opacity: 0.5,
        _hover: {
          cursor: 'not-allowed',
          color: 'brand.button',
          background: 'transparent',
        },
        ...(disabledOverride ?? {}),
      }}
      _hover={{ backgroundColor: hexToRGBA(brandButton, 0.1), ...(hoverOverride ?? {}) }}
    />
  );
});

OutlineButton.displayName = 'OutlineButton';

export default OutlineButton;
